import * as THREE from "three";

/**
 * Scale-figure layout — pure geometry, no React and no WebGL.
 *
 * The figures are a VIEWER annotation (see
 * docs/notes/2026-06-11-annotation-overlays-as-scene-components.md and
 * docs/notes/2026-07-26-photo-vs-analysis-mode-semantic-colours-belong-to-the-overlay.md):
 * the engines build the photo, the overlay layer adds meaning. So the
 * placement is derived from the scene the engines ACTUALLY built rather
 * than from a per-engine copy of the bench formulas — every engine names
 * its bench slats `detail/bench-slat/<benchId>-<n>` (see
 * `addSlattedBench` in src/engines/detailHelpers.ts), so measuring those
 * meshes with `Box3` follows all three engines for free and can never
 * drift out of sync with them.
 *
 * Sizes are measured with `Box3.setFromObject`, never `mesh.scale` —
 * sloped/sheared members bake their size into the geometry.
 */

/** Slat meshes of every bench in every engine carry this prefix. */
const BENCH_SLAT_PREFIX = "detail/bench-slat/";
/** The room floor slab. */
const FLOOR_PANEL_PREFIX = "detail/floor-panel/";
/** The stove assembly. */
const HEATER_PREFIX = "detail/heater/";
/**
 * The flue. Together with the stove it forms the keep-out set: nobody sits
 * inside either, so every seat whose figure would overlap one is dropped
 * (and not counted).
 */
const CHIMNEY_PREFIX = "detail/chimney/";
/**
 * Bench support frame — rails, ledgers and the cross joists carrying the
 * slats (see `addSlattedBench` in src/engines/detailHelpers.ts). The joists
 * hang directly under the seat top, spaced at roughly the same 0.6 m pitch
 * the figures use, so a joist and a seat regularly land within a few
 * centimetres of each other. Rather than clamp the shin to the frame (tried
 * once for the floor reference and rejected — see
 * docs/notes/2026-07-28-scale-figures-derive-placement-from-the-built-scene.md,
 * it shrinks the shin to ~8 cm), a colliding seat is nudged sideways along
 * the bench instead: the frame is only a few centimetres thick, so a small
 * shift clears it without visibly changing where the figure "sits".
 */
const BENCH_RAIL_PREFIX = "detail/bench-rail/";
/** Matches only the cross-joist meshes, e.g. `detail/bench-rail/square-upper-joist-2`. */
const BENCH_JOIST_NAME = /^detail\/bench-rail\/(.+)-joist-\d+$/;

/**
 * Presentation seat pitch. One adult per 0.6 m of bench length is the
 * rule of thumb Swedish sauna builders use for lav planning. This is a
 * viewer-side ESTIMATE for the scale figures only — it never reaches the
 * BOM, the BBR checks or any summary.
 */
export const SEAT_PITCH_M = 0.6;

/** Head-top height above the seat for a scaled adult, seated. */
export const FIGURE_SEATED_HEIGHT_M = 0.85;

/** Knee-to-heel drop when the feet hang free. */
export const FIGURE_SHIN_LENGTH_M = 0.42;
/** Shortest shin we still draw when the bench sits close to the floor. */
export const FIGURE_MIN_SHIN_LENGTH_M = 0.06;
/**
 * Vertical distance from the seat down to the top of the shin capsule.
 * Foot level = seat - (shinLength + this).
 */
export const FIGURE_ANKLE_OFFSET_M = 0.025;

/** Clearance kept between a figure's footprint and a keep-out volume. */
const KEEP_OUT_MARGIN_M = 0.05;

/** How far the hips sit in from the bench's back edge. */
const HIP_INSET_M = 0.18;

/* ── Figure proportions ──────────────────────────────────── */
/*
 * Measured up from the seat surface, in figure-local space where +Z is
 * the direction the figure looks. `ScaleFigures.tsx` builds its meshes
 * straight from `getFigurePartSpecs`, so the boxes used for keep-out
 * tests and the drawn geometry can never disagree.
 *
 * Only the two the proportions test asserts against
 * `FIGURE_SEATED_HEIGHT_M` are exported; the rest are internal to
 * `getFigurePartSpecs`, which is the single way anything outside this
 * module reads the figure's shape.
 */
export const FIGURE_HEAD_RADIUS = 0.1;
export const FIGURE_HEAD_CENTER_Y = 0.75;
const FIGURE_NECK_RADIUS = 0.045;
const FIGURE_NECK_LENGTH = 0.03;
const FIGURE_NECK_CENTER_Y = 0.615;
const FIGURE_TORSO_RADIUS = 0.13;
const FIGURE_TORSO_LENGTH = 0.3;
const FIGURE_TORSO_CENTER_Y = 0.3;
const FIGURE_THIGH_RADIUS = 0.1;
const FIGURE_THIGH_LENGTH = 0.2;
const FIGURE_THIGH_CENTER_Y = 0.1;
const FIGURE_THIGH_CENTER_Z = 0.145;
const FIGURE_SHIN_RADIUS = 0.075;
const FIGURE_SHIN_TOP_Y = 0.05;
const FIGURE_SHIN_CENTER_Z = 0.25;

type FigurePartId = "head" | "neck" | "torso" | "thigh" | "shin";

export interface FigurePartSpec {
	id: FigurePartId;
	shape: "sphere" | "capsule";
	radius: number;
	/** Cylinder section of a capsule; 0 for a sphere. */
	length: number;
	/** Capsule axis in figure-local space. */
	axis: "y" | "z";
	/** Centre in figure-local space. */
	center: [number, number, number];
}

/** The five primitives a figure is made of, for a given shin length. */
export function getFigurePartSpecs(shinLength: number): FigurePartSpec[] {
	return [
		{
			id: "head",
			shape: "sphere",
			radius: FIGURE_HEAD_RADIUS,
			length: 0,
			axis: "y",
			center: [0, FIGURE_HEAD_CENTER_Y, 0],
		},
		{
			id: "neck",
			shape: "capsule",
			radius: FIGURE_NECK_RADIUS,
			length: FIGURE_NECK_LENGTH,
			axis: "y",
			center: [0, FIGURE_NECK_CENTER_Y, 0],
		},
		{
			id: "torso",
			shape: "capsule",
			radius: FIGURE_TORSO_RADIUS,
			length: FIGURE_TORSO_LENGTH,
			axis: "y",
			center: [0, FIGURE_TORSO_CENTER_Y, 0],
		},
		{
			id: "thigh",
			shape: "capsule",
			radius: FIGURE_THIGH_RADIUS,
			length: FIGURE_THIGH_LENGTH,
			axis: "z",
			center: [0, FIGURE_THIGH_CENTER_Y, FIGURE_THIGH_CENTER_Z],
		},
		{
			id: "shin",
			shape: "capsule",
			radius: FIGURE_SHIN_RADIUS,
			length: shinLength,
			axis: "y",
			center: [0, FIGURE_SHIN_TOP_Y - shinLength / 2, FIGURE_SHIN_CENTER_Z],
		},
	];
}

type BenchZone = "sauna" | "relax";

/** Which way the figure looks; always axis-aligned with the bench. */
type FigureFacing = "+x" | "-x" | "+z" | "-z";

export interface BenchSurface {
	/** Bench name prefix as the engine wrote it, e.g. `square-upper`. */
	id: string;
	zone: BenchZone;
	/** World-space bounds of the seat slats. */
	box: THREE.Box3;
}

export interface ScaleFigurePlacement {
	key: string;
	benchId: string;
	zone: BenchZone;
	/** Hip position: on the seat surface, inset from the back edge. */
	position: [number, number, number];
	rotationY: number;
	facing: FigureFacing;
	/** Clamped so the feet never sink through the interior floor. */
	shinLength: number;
}

/** Presentation seat estimate surfaced next to the viewer toggle. */
export interface ScaleFigureCapacity {
	/** Seats on the sauna-room benches. */
	seats: number;
	/** Seats on relax-room benches (two-room only). */
	relaxSeats: number;
}

export interface ScaleFigurePlan extends ScaleFigureCapacity {
	figures: ScaleFigurePlacement[];
}

const FACING_VECTOR: Record<FigureFacing, { x: number; z: number }> = {
	"+x": { x: 1, z: 0 },
	"-x": { x: -1, z: 0 },
	"+z": { x: 0, z: 1 },
	"-z": { x: 0, z: -1 },
};

const FACING_ROTATION_Y: Record<FigureFacing, number> = {
	"+z": 0,
	"+x": Math.PI / 2,
	"-z": Math.PI,
	"-x": -Math.PI / 2,
};

/* ── Figure bounds ───────────────────────────────────────── */

function localPartBox(spec: FigurePartSpec): THREE.Box3 {
	const [cx, cy, cz] = spec.center;
	const r = spec.radius;
	const half = spec.length / 2 + r;
	const ex = r;
	const ey = spec.shape === "capsule" && spec.axis === "y" ? half : r;
	const ez = spec.shape === "capsule" && spec.axis === "z" ? half : r;
	return new THREE.Box3(
		new THREE.Vector3(cx - ex, cy - ey, cz - ez),
		new THREE.Vector3(cx + ex, cy + ey, cz + ez),
	);
}

/**
 * World-space bounds of every primitive of one figure. Used both by the
 * keep-out filter and by the tests that prove no body part ends up inside
 * the stove.
 */
export function getFigurePartBoxes(
	placement: ScaleFigurePlacement,
): Array<{ id: FigurePartId; box: THREE.Box3 }> {
	const forward = FACING_VECTOR[placement.facing];
	// The figure is only ever rotated about Y by a multiple of 90 degrees,
	// so the rotation maps local (x, z) onto world axes exactly — no
	// floating-point smear from a rotation matrix.
	const right = { x: forward.z, z: -forward.x };
	const [px, py, pz] = placement.position;

	return getFigurePartSpecs(placement.shinLength).map((spec) => {
		const local = localPartBox(spec);
		const worldPoints: THREE.Vector3[] = [];
		for (const lx of [local.min.x, local.max.x]) {
			for (const lz of [local.min.z, local.max.z]) {
				worldPoints.push(
					new THREE.Vector3(
						px + right.x * lx + forward.x * lz,
						0,
						pz + right.z * lx + forward.z * lz,
					),
				);
			}
		}
		const box = new THREE.Box3();
		box.makeEmpty();
		for (const point of worldPoints) box.expandByPoint(point);
		box.min.y = py + local.min.y;
		box.max.y = py + local.max.y;
		return { id: spec.id, box };
	});
}

/** Union of every primitive of one figure, in world space. */
function getFigureBounds(placement: ScaleFigurePlacement): THREE.Box3 {
	const box = new THREE.Box3();
	box.makeEmpty();
	for (const part of getFigurePartBoxes(placement)) box.union(part.box);
	return box;
}

/* ── Scene readers ───────────────────────────────────────── */

function isRenderable(object: THREE.Object3D, root: THREE.Object3D): boolean {
	let node: THREE.Object3D | null = object;
	while (node) {
		if (!node.visible) return false;
		if (node === root) return true;
		node = node.parent;
	}
	return true;
}

function benchIdFromMeshName(name: string, prefix: string): string {
	return name.slice(prefix.length).replace(/-\d+$/, "");
}

function emptyBox(): THREE.Box3 {
	const box = new THREE.Box3();
	box.makeEmpty();
	return box;
}

/**
 * Every measurement the planner takes off the built scene.
 *
 * The scene is walked ONCE. Bench surfaces, the floor slab, the stove and
 * the flue are four filters over the same node list, and a rebuild during
 * a dimension drag re-plans on every frame — so five passes over ~2 000
 * nodes (one per reader, plus four full `updateWorldMatrix` sweeps) was
 * four passes of pure duplication. The readers below are pure functions of
 * these buckets; only `readSceneBoxes` touches the scene graph.
 */
interface SceneBoxes {
	/** One seat box per bench id, from the VISIBLE slats only. */
	benches: Map<string, THREE.Box3>;
	/** Floor slab, measured regardless of layer visibility. */
	floor: THREE.Box3;
	/** Stove assembly, likewise. */
	heater: THREE.Box3;
	/** Flue, likewise. */
	chimney: THREE.Box3;
	/** Cross-joist boxes per bench id, from the VISIBLE frame only. */
	benchJoists: Map<string, THREE.Box3[]>;
}

function readSceneBoxes(root: THREE.Object3D | null | undefined): SceneBoxes {
	const boxes: SceneBoxes = {
		benches: new Map<string, THREE.Box3>(),
		floor: emptyBox(),
		heater: emptyBox(),
		chimney: emptyBox(),
		benchJoists: new Map<string, THREE.Box3[]>(),
	};
	if (!root) return boxes;
	root.updateWorldMatrix(true, true);

	root.traverse((object) => {
		const name = object.name;
		if (name.startsWith(BENCH_SLAT_PREFIX)) {
			// Benches hidden by a layer toggle are skipped, so switching the
			// interior layer off also removes the figures instead of leaving
			// them floating.
			if (!isRenderable(object, root)) return;
			const id = benchIdFromMeshName(name, BENCH_SLAT_PREFIX);
			let box = boxes.benches.get(id);
			if (!box) {
				box = emptyBox();
				boxes.benches.set(id, box);
			}
			box.expandByObject(object);
			return;
		}
		if (name.startsWith(FLOOR_PANEL_PREFIX)) {
			boxes.floor.expandByObject(object);
			return;
		}
		if (name.startsWith(HEATER_PREFIX)) {
			boxes.heater.expandByObject(object);
			return;
		}
		if (name.startsWith(CHIMNEY_PREFIX)) {
			boxes.chimney.expandByObject(object);
			return;
		}
		if (name.startsWith(BENCH_RAIL_PREFIX)) {
			const match = name.match(BENCH_JOIST_NAME);
			if (!match) return;
			if (!isRenderable(object, root)) return;
			const id = match[1];
			let list = boxes.benchJoists.get(id);
			if (!list) {
				list = [];
				boxes.benchJoists.set(id, list);
			}
			list.push(new THREE.Box3().setFromObject(object));
		}
	});

	return boxes;
}

function benchSurfacesFrom(benches: Map<string, THREE.Box3>): BenchSurface[] {
	return [...benches.entries()]
		.filter(([, box]) => !box.isEmpty())
		.map(([id, box]) => ({
			id,
			zone: (id.includes("relax") ? "relax" : "sauna") as BenchZone,
			box,
		}))
		.sort((a, b) => a.id.localeCompare(b.id));
}

/**
 * Collect one world-space seat box per bench from the live scene.
 *
 * Returns `[]` for an engine that builds no benches — the caller then
 * renders nothing rather than crashing.
 */
export function collectBenchSurfaces(
	root: THREE.Object3D | null | undefined,
): BenchSurface[] {
	return benchSurfacesFrom(readSceneBoxes(root).benches);
}

/**
 * Top surface of the floor slab, or `null` when the engine builds none.
 * Measured regardless of layer visibility: this is a reference level, not
 * something we draw.
 */
export function findFloorTopY(
	root: THREE.Object3D | null | undefined,
): number | null {
	const floor = readSceneBoxes(root).floor;
	return floor.isEmpty() ? null : floor.max.y;
}

/**
 * The level a person in this room would actually stand on.
 *
 * Two independent readings, the HIGHER of which wins:
 *
 *  - the drawn floor slab (`detail/floor-panel/`), and
 *  - the underside of the stove assembly.
 *
 * The stove is the reliable one, because every engine sets it down on the
 * real interior floor. The barrel dishes its slab up to 0.47 m below the
 * level it lays its interior out from, so the slab alone would put the
 * feet through the shell. The slab is still needed as the other half of
 * the `max`, because the square/two-room ELECTRIC heater reaches ~0.09 m
 * below the floor panel.
 *
 * Bench posts are deliberately NOT consulted. `detail/bench-leg/` cannot
 * tell a post that STANDS on something (the barrel's benches) from a
 * decorative stub that HANGS in mid-air (the square/two-room lower lav,
 * whose 0.2 m posts stop 0.37 m above the floor) — and clamping to a
 * hanging stub halves the shins of the one bench present in every square
 * and two-room design, right beside an upper lav with full-length ones.
 *
 * `null` when the scene offers neither reading: the feet then hang at
 * their nominal length, which errs toward "floating" rather than
 * "through the floor".
 */
export function findInteriorFloorY(
	root: THREE.Object3D | null | undefined,
): number | null {
	return interiorFloorYFrom(readSceneBoxes(root));
}

function interiorFloorYFrom(boxes: SceneBoxes): number | null {
	const slabTopY = boxes.floor.isEmpty() ? null : boxes.floor.max.y;
	const heaterBottomY = boxes.heater.isEmpty() ? null : boxes.heater.min.y;

	if (slabTopY === null) return heaterBottomY;
	if (heaterBottomY === null) return slabTopY;
	return Math.max(slabTopY, heaterBottomY);
}

/**
 * Volumes no figure may overlap: the stove assembly and the flue. One
 * union box per assembly — deliberately conservative, since a seat lost
 * to a slightly generous stove box is far cheaper than a figure sitting
 * inside it.
 */
export function collectKeepOutVolumes(
	root: THREE.Object3D | null | undefined,
): THREE.Box3[] {
	return keepOutVolumesFrom(readSceneBoxes(root));
}

function keepOutVolumesFrom(boxes: SceneBoxes): THREE.Box3[] {
	return [boxes.heater, boxes.chimney].filter((box) => !box.isEmpty());
}

/* ── Planning ────────────────────────────────────────────── */

interface ScaleFigurePlanInput {
	benches: BenchSurface[];
	/**
	 * The level the feet stop at, from `findInteriorFloorY`. `null` keeps
	 * the nominal free-hanging shin.
	 */
	interiorFloorY: number | null;
	/** Figures turn away from the nearest wall, i.e. toward this point. */
	roomCenter: { x: number; z: number };
	/** Stove/flue volumes; seats overlapping one are dropped. */
	keepOut?: THREE.Box3[];
	/**
	 * Cross-joist boxes per bench id. A seat whose legs would land on a joist
	 * is nudged sideways along the bench rather than dropped — see
	 * `BENCH_RAIL_PREFIX` above.
	 */
	benchJoists?: Map<string, THREE.Box3[]>;
}

function overlapsKeepOut(
	placement: ScaleFigurePlacement,
	keepOut: THREE.Box3[],
): boolean {
	if (keepOut.length === 0) return false;
	// XZ footprint only: a flue runs the full height of the room and a
	// stove is hot in every direction, so "beside it but at another
	// height" is not a seat we want to offer.
	const bounds = getFigureBounds(placement);
	const minX = bounds.min.x - KEEP_OUT_MARGIN_M;
	const maxX = bounds.max.x + KEEP_OUT_MARGIN_M;
	const minZ = bounds.min.z - KEEP_OUT_MARGIN_M;
	const maxZ = bounds.max.z + KEEP_OUT_MARGIN_M;
	return keepOut.some(
		(box) =>
			minX <= box.max.x &&
			maxX >= box.min.x &&
			minZ <= box.max.z &&
			maxZ >= box.min.z,
	);
}

/** How far a seat is nudged along the bench to dodge a joist underneath it. */
const JOIST_DODGE_OFFSETS_M = [0.15, -0.15];

const LEG_PART_IDS: ReadonlySet<FigurePartId> = new Set(["thigh", "shin"]);

function overlapsJoists(
	placement: ScaleFigurePlacement,
	joists: THREE.Box3[],
): boolean {
	if (joists.length === 0) return false;
	// Only the legs, checked individually — not the whole-body union bounds.
	// The torso is wider than the legs (0.13 m vs 0.075-0.1 m radius) but
	// sits well above the frame; unioning it into the same box as the legs
	// widens the X test enough to report a collision that isn't really
	// there once the legs have cleared the joist.
	const legBoxes = getFigurePartBoxes(placement).filter((part) =>
		LEG_PART_IDS.has(part.id),
	);
	return legBoxes.some(({ box: legBox }) =>
		joists.some(
			(box) =>
				legBox.min.x <= box.max.x &&
				legBox.max.x >= box.min.x &&
				legBox.min.y <= box.max.y &&
				legBox.max.y >= box.min.y &&
				legBox.min.z <= box.max.z &&
				legBox.max.z >= box.min.z,
		),
	);
}

/**
 * If `placement`'s legs land on one of this bench's own joists, try shifting
 * it a little way along the bench. Returns the first offset that clears the
 * joists, or the original placement if none do — a seat that still clips is
 * better than one silently dropped, since a missing seat re-opens the
 * capacity mismatch this module is careful to avoid elsewhere.
 */
function dodgeJoists(
	placement: ScaleFigurePlacement,
	joists: THREE.Box3[],
	alongX: boolean,
): ScaleFigurePlacement {
	if (!overlapsJoists(placement, joists)) return placement;
	for (const offset of JOIST_DODGE_OFFSETS_M) {
		const [x, y, z] = placement.position;
		const nudged: ScaleFigurePlacement = {
			...placement,
			position: alongX ? [x + offset, y, z] : [x, y, z + offset],
		};
		if (!overlapsJoists(nudged, joists)) return nudged;
	}
	return placement;
}

/**
 * Lay out the figures along every bench top.
 *
 * Seats per bench start at floor(benchLength / 0.6). The run is centred
 * on the bench, so the leftover length is split evenly between the two
 * ends and every figure keeps at least 0.3 m to the bench ENDS — but that
 * says nothing about what stands in front of the bench, so each candidate
 * seat is then tested against the stove/flue keep-out volumes and dropped
 * if it would overlap one. The reported capacity counts what is left.
 *
 * Returns `null` only when there is no bench at all; a bench too short for
 * a single seat still yields a plan reporting zero seats, so the viewer
 * can say "0" instead of going silent.
 */
export function planScaleFigures(
	input: ScaleFigurePlanInput,
): ScaleFigurePlan | null {
	const {
		benches,
		interiorFloorY,
		roomCenter,
		keepOut = [],
		benchJoists,
	} = input;
	if (benches.length === 0) return null;

	const figures: ScaleFigurePlacement[] = [];
	let seats = 0;
	let relaxSeats = 0;

	for (const bench of benches) {
		const size = bench.box.getSize(new THREE.Vector3());
		const center = bench.box.getCenter(new THREE.Vector3());
		if (!Number.isFinite(size.x) || !Number.isFinite(size.z)) continue;

		const alongX = size.x >= size.z;
		const length = alongX ? size.x : size.z;
		const depth = alongX ? size.z : size.x;
		const count = Math.floor(length / SEAT_PITCH_M);
		if (count < 1) continue;

		const facing: FigureFacing = alongX
			? center.z <= roomCenter.z
				? "+z"
				: "-z"
			: center.x <= roomCenter.x
				? "+x"
				: "-x";
		const forward = FACING_VECTOR[facing];
		// Sit the hips in from the back edge so the thighs land on the seat
		// instead of hanging off the front of it.
		const backShift = Math.max(0, depth / 2 - Math.min(HIP_INSET_M, depth / 2));
		const acrossCenter = alongX ? center.z : center.x;
		const acrossPos =
			acrossCenter - (alongX ? forward.z : forward.x) * backShift;

		const seatY = bench.box.max.y;
		// Lower legs hang at their nominal length and are shortened ONLY
		// when the interior floor is actually in the way. A high lav keeps
		// full-length shins (feet dangling, as they would in life); a
		// near-floor barrel bench has them cut to land on the floor.
		const shinLength =
			interiorFloorY === null
				? FIGURE_SHIN_LENGTH_M
				: Math.min(
						FIGURE_SHIN_LENGTH_M,
						Math.max(
							FIGURE_MIN_SHIN_LENGTH_M,
							seatY - interiorFloorY - FIGURE_ANKLE_OFFSET_M,
						),
					);

		const alongCenter = alongX ? center.x : center.z;
		const span = (count - 1) * SEAT_PITCH_M;
		const joists = benchJoists?.get(bench.id) ?? [];
		let placed = 0;
		for (let index = 0; index < count; index += 1) {
			const alongPos = alongCenter - span / 2 + index * SEAT_PITCH_M;
			let placement: ScaleFigurePlacement = {
				key: `${bench.id}-${index + 1}`,
				benchId: bench.id,
				zone: bench.zone,
				position: alongX
					? [alongPos, seatY, acrossPos]
					: [acrossPos, seatY, alongPos],
				rotationY: FACING_ROTATION_Y[facing],
				facing,
				shinLength,
			};
			placement = dodgeJoists(placement, joists, alongX);
			if (overlapsKeepOut(placement, keepOut)) continue;
			figures.push(placement);
			placed += 1;
		}

		if (bench.zone === "relax") {
			relaxSeats += placed;
		} else {
			seats += placed;
		}
	}

	return { figures, seats, relaxSeats };
}

/**
 * Read the live scene and produce a figure plan, or `null` when the scene
 * has no benches (a hidden interior layer or a step view legitimately
 * leaves none on screen).
 */
export function planScaleFiguresForScene(
	root: THREE.Object3D | null | undefined,
): ScaleFigurePlan | null {
	// One walk of the scene graph feeds all four readers below.
	const boxes = readSceneBoxes(root);
	const benches = benchSurfacesFrom(boxes.benches);
	if (benches.length === 0) return null;

	return planScaleFigures({
		benches,
		interiorFloorY: interiorFloorYFrom(boxes),
		roomCenter: resolveRoomCenter(boxes.floor, benches),
		keepOut: keepOutVolumesFrom(boxes),
		benchJoists: boxes.benchJoists,
	});
}

/**
 * Cap the total figure count at `maxTotal`, keeping the first N in planning
 * order (benches sorted by id, seats from each bench's centre outward). Used
 * to keep the viewer's estimate from exceeding a capacity the user actually
 * chose (the wizard's "2/4/6 personer" tier) — that number never reaches
 * engine geometry, so without this the estimate is free to land above it
 * whenever both benches' 0.6 m seat pitch adds up to more seats than the
 * room was sized for.
 *
 * `maxTotal` of `null`/`undefined` (no wizard hint, e.g. an old share link)
 * leaves the plan untouched.
 */
export function capScaleFigurePlan(
	plan: ScaleFigurePlan,
	maxTotal: number | null | undefined,
): ScaleFigurePlan {
	if (
		maxTotal === null ||
		maxTotal === undefined ||
		!Number.isFinite(maxTotal) ||
		maxTotal < 0 ||
		plan.figures.length <= maxTotal
	) {
		return plan;
	}

	const figures = plan.figures.slice(0, maxTotal);
	let seats = 0;
	let relaxSeats = 0;
	for (const figure of figures) {
		if (figure.zone === "relax") relaxSeats += 1;
		else seats += 1;
	}
	return { figures, seats, relaxSeats };
}

/**
 * The point the figures turn toward. The floor slab spans the whole
 * building and is centred on it, so its centre is the most stable "into
 * the room" reference available from the built scene; the bench spread is
 * the fallback for engines that build no floor slab.
 */
function resolveRoomCenter(
	floorBox: THREE.Box3,
	benches: BenchSurface[],
): { x: number; z: number } {
	if (!floorBox.isEmpty()) {
		const center = floorBox.getCenter(new THREE.Vector3());
		return { x: center.x, z: center.z };
	}

	const union = new THREE.Box3();
	union.makeEmpty();
	for (const bench of benches) union.union(bench.box);
	if (union.isEmpty()) return { x: 0, z: 0 };
	const center = union.getCenter(new THREE.Vector3());
	return { x: center.x, z: center.z };
}
